/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Typography,
  Space,
  Badge,
  Button,
  Table,
  Tooltip,
  Alert,
} from "antd";
import {
  ThunderboltOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
  ReloadOutlined,
  AimOutlined,
  FireOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { config } from "../config/env";

const { Title, Text } = Typography;

export interface TelemetryData {
  vehicleId: string;
  timestampUs: number;
  speedMps: number;
  headingDeg: number;
  batteryVoltage: number;
  batteryCurrent: number;
  motorCurrent: number;
  motorRpm: number;
  coilAlignmentMm: number;
  coilCouplingCoeff: number;
  harvestedPowerW: number;
  trackPowerW: number;
  efficiencyPct: number;
  temperatureC: number;
  airGapMm: number;
  position?: { latitude: number; longitude: number; altitudeM: number };
  state: "UNKNOWN" | "IDLE" | "DRIVING" | "CHARGING" | "ALIGNING" | "FAULT" | "OFFLINE";
}

export interface PowerHarvestData {
  vehicleId: string;
  coilId: string;
  timestampUs: number;
  txFrequencyHz: number;
  txPowerW: number;
  rxVoltageV: number;
  rxCurrentA: number;
  rxPowerW: number;
  supercapVoltageV: number;
  supercapSocPct: number;
  couplingCoefficient: number;
  qualityFactor: number;
  airGapMm: number;
  alignmentOffsetMm: number;
  efficiencyPct: number;
  coilTemperatureC: number;
  rectifierTemperatureC: number;
}

export interface VehicleStatus {
  vehicleId: string;
  name: string;
  state: string;
  lastTelemetry: TelemetryData | null;
  lastPowerHarvest: PowerHarvestData | null;
  uptimeMs: number;
  totalEnergyHarvestedWh: number;
  totalDistanceM: number;
}

export const WptFleetDashboard: React.FC = () => {
  const [fleet, setFleet] = useState<VehicleStatus[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [isLive, setIsLive] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [powerHistory, setPowerHistory] = useState<
    Array<{ time: string; rxPower: number; txPower: number; efficiency: number }>
  >([]);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchFleetStatus = React.useCallback(async () => {
    try {
      const url = `${config.apiUrl}/api/vehicles/fleet`;
      const res = await axios.get<VehicleStatus[]>(url, { timeout: 3000 });
      if (Array.isArray(res.data) && res.data.length > 0) {
        setFleet(res.data);
        setLastUpdated(new Date());

        if (!selectedVehicleId && res.data[0]) {
          setSelectedVehicleId(res.data[0].vehicleId);
        }

        const target = res.data.find((v) => v.vehicleId === selectedVehicleId) || res.data[0];
        if (target && target.lastTelemetry) {
          const nowStr = new Date().toLocaleTimeString();
          setPowerHistory((prev) => [
            ...prev.slice(-19),
            {
              time: nowStr,
              rxPower: Number(target.lastTelemetry?.harvestedPowerW?.toFixed(1) || 0),
              txPower: Number(target.lastTelemetry?.trackPowerW?.toFixed(1) || 0),
              efficiency: Number(target.lastTelemetry?.efficiencyPct?.toFixed(1) || 0),
            },
          ]);
        }
      }
    } catch {
      // Backend polling error or endpoint empty
    }
  }, [selectedVehicleId]);

  useEffect(() => {
    fetchFleetStatus();
    if (isLive) {
      pollTimerRef.current = setInterval(fetchFleetStatus, 1000);
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [isLive, fetchFleetStatus]);

  const activeVehicle = useMemo(() => {
    if (!fleet.length) {
      const simStatus: VehicleStatus = {
        vehicleId: "sim_vehicle_001",
        name: "ScaleBot-01 (Active)",
        state: "CHARGING",
        lastTelemetry: {
          vehicleId: "sim_vehicle_001",
          timestampUs: Date.now() * 1000,
          speedMps: 0.32,
          headingDeg: 89.4,
          batteryVoltage: 4.82,
          batteryCurrent: 3.8,
          motorCurrent: 1.25,
          motorRpm: 450,
          coilAlignmentMm: 2.1,
          coilCouplingCoeff: 0.28,
          harvestedPowerW: 18.3,
          trackPowerW: 24.5,
          efficiencyPct: 74.7,
          temperatureC: 38.6,
          airGapMm: 11.8,
          state: "CHARGING",
        },
        lastPowerHarvest: {
          vehicleId: "sim_vehicle_001",
          coilId: "rx_coil_primary",
          timestampUs: Date.now() * 1000,
          txFrequencyHz: 85000,
          txPowerW: 25.0,
          rxVoltageV: 4.82,
          rxCurrentA: 3.8,
          rxPowerW: 18.3,
          supercapVoltageV: 4.85,
          supercapSocPct: 88,
          couplingCoefficient: 0.28,
          qualityFactor: 320,
          airGapMm: 11.8,
          alignmentOffsetMm: 2.1,
          efficiencyPct: 74.7,
          coilTemperatureC: 36.2,
          rectifierTemperatureC: 41.5,
        },
        uptimeMs: 142000,
        totalEnergyHarvestedWh: 4.86,
        totalDistanceM: 45.4,
      };
      return simStatus;
    }
    return fleet.find((v) => v.vehicleId === selectedVehicleId) || fleet[0];
  }, [fleet, selectedVehicleId]);

  const telemetry = activeVehicle.lastTelemetry;
  const powerHarvest = activeVehicle.lastPowerHarvest;

  const supercapV = powerHarvest?.supercapVoltageV ?? telemetry?.batteryVoltage ?? 0;
  const supercapSoc = Math.min(
    100,
    Math.max(0, Math.round(((supercapV - 0.5) / (5.5 - 0.5)) * 100))
  );

  const alignmentMm = telemetry?.coilAlignmentMm ?? 0;
  const alignmentAbs = Math.abs(alignmentMm);
  const alignmentStatus =
    alignmentAbs <= 5 ? "Optimal" : alignmentAbs <= 15 ? "Acceptable" : "Misaligned";
  const alignmentColor =
    alignmentAbs <= 5 ? "#10b981" : alignmentAbs <= 15 ? "#f59e0b" : "#ef4444";

  const clampedOffsetPercent = Math.min(100, Math.max(0, ((alignmentMm + 25) / 50) * 100));

  const stateTagColor = (state: string) => {
    switch (state) {
      case "CHARGING":
        return "success";
      case "DRIVING":
        return "processing";
      case "ALIGNING":
        return "warning";
      case "FAULT":
        return "error";
      default:
        return "default";
    }
  };

  const tableColumns = [
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      render: (id: string, record: VehicleStatus) => (
        <Button
          type={record.vehicleId === activeVehicle.vehicleId ? "primary" : "link"}
          size="small"
          onClick={() => setSelectedVehicleId(record.vehicleId)}
        >
          {record.name || id}
        </Button>
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (s: string) => <Tag color={stateTagColor(s)}>{s}</Tag>,
    },
    {
      title: "Harvest (W)",
      key: "harvest",
      render: (_: any, r: VehicleStatus) => (
        <Text style={{ color: "#10b981", fontWeight: 600 }}>
          {r.lastTelemetry?.harvestedPowerW?.toFixed(1) ?? "0.0"} W
        </Text>
      ),
    },
    {
      title: "Efficiency",
      key: "efficiency",
      render: (_: any, r: VehicleStatus) => (
        <Text style={{ color: "#3b82f6" }}>
          {r.lastTelemetry?.efficiencyPct?.toFixed(1) ?? "0.0"}%
        </Text>
      ),
    },
    {
      title: "Supercap (V)",
      key: "supercap",
      render: (_: any, r: VehicleStatus) => (
        <Text style={{ color: "#e2e8f0" }}>
          {r.lastPowerHarvest?.supercapVoltageV?.toFixed(2) ??
            r.lastTelemetry?.batteryVoltage?.toFixed(2) ??
            "0.00"}{" "}
          V
        </Text>
      ),
    },
    {
      title: "Alignment Offset",
      key: "alignment",
      render: (_: any, r: VehicleStatus) => {
        const offset = r.lastTelemetry?.coilAlignmentMm ?? 0;
        return (
          <Tag color={Math.abs(offset) <= 5 ? "green" : Math.abs(offset) <= 15 ? "orange" : "red"}>
            {offset >= 0 ? `+${offset.toFixed(1)}` : offset.toFixed(1)} mm
          </Tag>
        );
      },
    },
    {
      title: "Speed (m/s)",
      key: "speed",
      render: (_: any, r: VehicleStatus) => (
        <Text>{r.lastTelemetry?.speedMps?.toFixed(2) ?? "0.00"} m/s</Text>
      ),
    },
    {
      title: "Total Harvest",
      key: "totalEnergy",
      render: (_: any, r: VehicleStatus) => (
        <Text>{(r.totalEnergyHarvestedWh * 1000).toFixed(1)} mWh</Text>
      ),
    },
  ];

  return (
    <div style={{ color: "#f8fafc" }}>
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <Title level={2} style={{ color: "#ffffff", margin: 0 }}>
            Dynamic WPT & Fleet Telemetry
          </Title>
          <Text style={{ color: "#94a3b8" }}>
            Real-time closed-loop inductive wireless power transfer & chassis motion control (85 kHz)
          </Text>
        </div>

        <Space>
          <Badge
            status={isLive ? "processing" : "default"}
            text={
              <Text style={{ color: isLive ? "#10b981" : "#94a3b8" }}>
                {isLive ? "LIVE TELEMETRY STREAM" : "STREAM PAUSED"}
              </Text>
            }
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchFleetStatus();
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            type={isLive ? "primary" : "default"}
            danger={isLive}
            onClick={() => setIsLive(!isLive)}
          >
            {isLive ? "Pause" : "Resume"}
          </Button>
        </Space>
      </div>

      {/* Top Banner Alert / Status */}
      <Alert
        message={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <strong>Target Vehicle:</strong> {activeVehicle.name} ({activeVehicle.vehicleId}) —{" "}
              <Tag color={stateTagColor(activeVehicle.state)}>{activeVehicle.state}</Tag>
              Carrier DSP: 85 kHz Goertzel Filter | TMC2209 PIT 25 kHz ISR | CAN 500 kbps
            </span>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              Last sync: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
        }
        type="info"
        showIcon
        style={{
          marginBottom: 24,
          background: "#0f172a",
          border: "1px solid #1e293b",
          color: "#cbd5e1",
        }}
      />

      {/* Primary KPI Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <Statistic
              title={<span style={{ color: "#94a3b8" }}>Harvested Power</span>}
              value={telemetry?.harvestedPowerW?.toFixed(1) ?? "0.0"}
              suffix="W"
              valueStyle={{ color: "#10b981", fontWeight: 700, fontSize: 28 }}
              prefix={<ThunderboltOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
              Track Transmitter: {telemetry?.trackPowerW?.toFixed(1) ?? "0.0"} W
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <Statistic
              title={<span style={{ color: "#94a3b8" }}>Transfer Efficiency (η)</span>}
              value={telemetry?.efficiencyPct?.toFixed(1) ?? "0.0"}
              suffix="%"
              valueStyle={{
                color:
                  (telemetry?.efficiencyPct ?? 0) >= 70
                    ? "#3b82f6"
                    : (telemetry?.efficiencyPct ?? 0) >= 50
                    ? "#f59e0b"
                    : "#ef4444",
                fontWeight: 700,
                fontSize: 28,
              }}
              prefix={<SafetyCertificateOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
              Coupling (k): {telemetry?.coilCouplingCoeff?.toFixed(3) ?? "0.000"} | Q:{" "}
              {powerHarvest?.qualityFactor ?? 310}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <Statistic
              title={<span style={{ color: "#94a3b8" }}>Supercapacitor SoC</span>}
              value={supercapSoc}
              suffix="%"
              valueStyle={{ color: "#a855f7", fontWeight: 700, fontSize: 28 }}
              prefix={<DashboardOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
              Terminal Voltage: {supercapV.toFixed(2)} V / 5.50 V max
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <Statistic
              title={<span style={{ color: "#94a3b8" }}>Lateral Offset</span>}
              value={alignmentMm >= 0 ? `+${alignmentMm.toFixed(1)}` : alignmentMm.toFixed(1)}
              suffix="mm"
              valueStyle={{ color: alignmentColor, fontWeight: 700, fontSize: 28 }}
              prefix={<AimOutlined />}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
              Status: <Tag color={alignmentAbs <= 5 ? "green" : "orange"}>{alignmentStatus}</Tag> |
              Air Gap: {telemetry?.airGapMm?.toFixed(1) ?? "12.0"} mm
            </div>
          </Card>
        </Col>
      </Row>

      {/* Visual Alignment & Supercapacitor Gauge Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Dynamic Coil Cross-Track Visualizer */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space>
                <AimOutlined style={{ color: "#38bdf8" }} />
                <span style={{ color: "#ffffff" }}>
                  Coil Cross-Track Dynamic Alignment (±25 mm Range)
                </span>
              </Space>
            }
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <div style={{ padding: "12px 8px" }}>
              <div
                style={{
                  height: 64,
                  background: "#030712",
                  borderRadius: 8,
                  position: "relative",
                  border: "1px solid #1e293b",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "40%",
                    width: "20%",
                    height: "100%",
                    background: "rgba(16, 185, 129, 0.15)",
                    borderLeft: "1px dashed rgba(16, 185, 129, 0.4)",
                    borderRight: "1px dashed rgba(16, 185, 129, 0.4)",
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: "#38bdf8",
                    opacity: 0.6,
                  }}
                />

                <div
                  style={{
                    position: "absolute",
                    left: `calc(${clampedOffsetPercent}% - 22px)`,
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: `3px solid ${alignmentColor}`,
                    background: "rgba(30, 41, 59, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 0 16px ${alignmentColor}66`,
                    transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <CarOutlined style={{ color: alignmentColor, fontSize: 18 }} />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "#64748b",
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                <span>-25 mm (Left)</span>
                <span style={{ color: "#38bdf8", fontWeight: 600 }}>0 mm (Track Boresight)</span>
                <span>+25 mm (Right)</span>
              </div>

              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: "#111827",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Space>
                  {alignmentAbs <= 5 ? (
                    <CheckCircleOutlined style={{ color: "#10b981", fontSize: 18 }} />
                  ) : (
                    <ExclamationCircleOutlined style={{ color: "#f59e0b", fontSize: 18 }} />
                  )}
                  <span style={{ color: "#e2e8f0", fontSize: 13 }}>
                    {alignmentAbs <= 5
                      ? "Receiver is centered within the inductive flux cone (>90% coupling retention)."
                      : alignmentMm > 0
                      ? `Steer LEFT by ${alignmentMm.toFixed(1)} mm to restore optimum coupling.`
                      : `Steer RIGHT by ${Math.abs(alignmentMm).toFixed(1)} mm to restore optimum coupling.`}
                  </span>
                </Space>
                <Tag color={alignmentColor}>{alignmentStatus}</Tag>
              </div>
            </div>
          </Card>
        </Col>

        {/* Supercapacitor & Thermal Buffer */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <DashboardOutlined style={{ color: "#a855f7" }} />
                <span style={{ color: "#ffffff" }}>Supercapacitor Buffer & Thermal Profile</span>
              </Space>
            }
            style={{
              background: "#0a0f1d",
              border: "1px solid #1e293b",
              borderRadius: 8,
            }}
          >
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <Progress
                type="circle"
                percent={supercapSoc}
                strokeColor={{
                  "0%": "#8b5cf6",
                  "100%": "#d946ef",
                }}
                strokeWidth={10}
                size={140}
                format={(pct) => (
                  <div style={{ color: "#f8fafc" }}>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{pct}%</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{supercapV.toFixed(2)} V</div>
                  </div>
                )}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginTop: 16,
              }}
            >
              <div style={{ background: "#111827", padding: 10, borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  <FireOutlined style={{ color: "#f97316", marginRight: 4 }} />
                  Coil Temperature
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#f8fafc", marginTop: 4 }}>
                  {telemetry?.temperatureC?.toFixed(1) ?? "35.0"} °C
                </div>
              </div>

              <div style={{ background: "#111827", padding: 10, borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  <FireOutlined style={{ color: "#ef4444", marginRight: 4 }} />
                  Rectifier Temp
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#f8fafc", marginTop: 4 }}>
                  {powerHarvest?.rectifierTemperatureC?.toFixed(1) ?? "38.2"} °C
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Real-Time Power Harvest History Strip */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined style={{ color: "#10b981" }} />
            <span style={{ color: "#ffffff" }}>Live Power Transfer Telemetry Feed</span>
          </Space>
        }
        style={{
          background: "#0a0f1d",
          border: "1px solid #1e293b",
          borderRadius: 8,
          marginBottom: 24,
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              height: 120,
              padding: "16px 8px 8px 8px",
              background: "#030712",
              borderRadius: 6,
              minWidth: 600,
            }}
          >
            {powerHistory.map((item, idx) => {
              const maxScale = 40;
              const heightPercent = Math.min(100, Math.max(10, (item.rxPower / maxScale) * 100));
              return (
                <Tooltip
                  key={idx}
                  title={`${item.time}: Rx ${item.rxPower}W / Tx ${item.txPower}W (${item.efficiency}%)`}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: `${heightPercent}%`,
                        background:
                          item.efficiency >= 70
                            ? "linear-gradient(180deg, #10b981 0%, #047857 100%)"
                            : "linear-gradient(180deg, #f59e0b 0%, #b45309 100%)",
                        borderRadius: "4px 4px 0 0",
                        transition: "height 0.3s ease",
                      }}
                    />
                    <div
                      style={{
                        fontSize: 9,
                        color: "#64748b",
                        marginTop: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.rxPower}W
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Multi-Vehicle Fleet Grid Table */}
      <Card
        title={
          <Space>
            <CarOutlined style={{ color: "#38bdf8" }} />
            <span style={{ color: "#ffffff" }}>Fleet Vehicles & Testbed Nodes</span>
          </Space>
        }
        style={{
          background: "#0a0f1d",
          border: "1px solid #1e293b",
          borderRadius: 8,
        }}
      >
        <Table
          dataSource={fleet.length > 0 ? fleet : [activeVehicle]}
          columns={tableColumns}
          rowKey="vehicleId"
          pagination={false}
          style={{ background: "transparent" }}
        />
      </Card>
    </div>
  );
};

export default WptFleetDashboard;
